import React, { useState } from 'react';
import { signUp } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { validateDateOfBirth } from '../common/utils';
import ImprovedDatePicker from '../common/components/Form/ImprovedDatePicker';

type SignUpParameters = {
    username: string;
    password: string;
    given_name: string;
    family_name: string;
    email: string;
    phone_number: string;
    birthdate: string;
};

const RegistrationForm: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [hasStartedTypingPassword, setHasStartedTypingPassword] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [passwordRules, setPasswordRules] = useState({
        lowercase: false,
        uppercase: false,
        number: false,
        length: false,
        specialChar: false,
        noLeadingTrailingSpaces: false,
    });
    const [signupError, setSignupError] = useState('');
    const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
    const [isDateOfBirthValid, setIsDateOfBirthValid] = useState(false);

    const validatePassword = (password: string) => {
        const rules = {
            lowercase: /[a-z]/.test(password),
            uppercase: /[A-Z]/.test(password),
            number: /\d/.test(password),
            length: password.length >= 10,
            specialChar: /[!@#$%^&*(),.?":{}|<>\s]/.test(password),
            noLeadingTrailingSpaces: !(/(^\s|\s$)/.test(password)),
        };
        setPasswordRules(rules);
        return Object.values(rules).every(Boolean);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setHasStartedTypingPassword(newPassword.length > 0);
        setIsPasswordValid(validatePassword(newPassword));
        setPasswordsMatch(newPassword === confirmPassword);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        setPasswordsMatch(newConfirmPassword === password);
    };

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPhoneNumber = e.target.value;
        setPhoneNumber(newPhoneNumber);
        setIsPhoneNumberValid(newPhoneNumber.length === 10);
    };

    const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDateOfBirth = e.target.value;
        setDateOfBirth(newDateOfBirth);
        setIsDateOfBirthValid(validateDateOfBirth(newDateOfBirth));
    };

    const getErrorMessage = (errorName: string): string => {
        const errorMessages: Record<string, string> = {
            "UsernameExistsException": 'An account with the given email already exists.',
            "InvalidPasswordException": 'The provided password is invalid.', // NOSONAR
            "CodeDeliveryFailureException": 'Failed to deliver the verification code.',
            "ForbiddenException": 'Request forbidden by AWS WAF.',
            "InvalidEmailRoleAccessPolicyException": 'Amazon Cognito is not allowed to use your email identity.',
            "InvalidLambdaResponseException": 'Invalid AWS Lambda response.',
            "InvalidParameterException": 'Invalid parameter provided to Amazon Cognito.',
            "InvalidSmsRoleAccessPolicyException": 'Invalid SMS role access policy.',
            "InvalidSmsRoleTrustRelationshipException": 'Invalid SMS role trust relationship.',
            "NotAuthorizedException": 'User is not authorized.',
            "ResourceNotFoundException": 'Requested resource not found.',
            "TooManyRequestsException": 'Too many requests. Please try again later.',
            "UnexpectedLambdaException": 'Unexpected error with AWS Lambda.',
            "UserLambdaValidationException": 'User validation error with AWS Lambda.',
        };
        return errorMessages[errorName] || 'An internal error occurred. Please try again later.';
    };

    const navigateToLogin = () => {
        navigate('/login');
    };

    const navigateToVerification = (email: string) => {
        navigate('/verify', { state: { email } });
    };

    const handleUnexpectedSignUpStep = (nextStep: { signUpStep?: string }) => {
        console.error('Sign-up not complete. Next step:', nextStep);
    };

    const handleSignUp = async ({ username, password, email, phone_number, given_name, family_name, birthdate }: SignUpParameters) => {
        try {
            const { isSignUpComplete, nextStep } = await signUp({
                username,
                password,
                options: {
                    userAttributes: {
                        email,
                        phone_number,
                        given_name,
                        family_name,
                        birthdate,
                    },
                    autoSignIn: true,
                },
            });
            
            if (isSignUpComplete) {
                navigateToLogin();
            } else if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
                navigateToVerification(email);
            } else {
                handleUnexpectedSignUpStep(nextStep);
            }
        } catch (error) {
            if (error instanceof Error) {
                const errorName = (error as Error & { name?: string }).name || 'UnknownError';
                setSignupError(getErrorMessage(errorName));
            } else {
                console.error('Error signing up:', error);
            }
        }
    };

    const renderPasswordRules = () => {
        const rules = [
            { key: 'lowercase', label: 'Password must contain a lower case letter', isValid: passwordRules.lowercase },
            { key: 'uppercase', label: 'Password must contain an upper case letter', isValid: passwordRules.uppercase },
            { key: 'number', label: 'Password must contain a number', isValid: passwordRules.number },
            { key: 'length', label: 'Password must contain at least 10 characters', isValid: passwordRules.length },
            { key: 'specialChar', label: 'Password must contain a special character or a space', isValid: passwordRules.specialChar },
            { key: 'noLeadingTrailingSpaces', label: 'Password must not contain a leading or trailing space', isValid: passwordRules.noLeadingTrailingSpaces },
        ];

        return (
            <ul className="mt-2 text-sm text-gray-600">
                {rules.map((rule) => (
                    <li key={rule.key} className={rule.isValid ? 'text-green-500' : 'text-red-500'}>
                        <span>{rule.isValid ? '\u2713' : '\u2715'}</span> {rule.label}
                    </li>
                ))}
            </ul>
        );
    };

    const isFormValid = (): boolean => {
        return validatePassword(password) && isPhoneNumberValid && isDateOfBirthValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid()) {
            return;
        }
        const formattedPhoneNumber = `+1${phoneNumber}`;
        await handleSignUp({
            username: email,
            password,
            email,
            phone_number: formattedPhoneNumber,
            given_name: firstName,
            family_name: lastName,
            birthdate: dateOfBirth,
        });
    };

    return (
        <div className="min-h-screen bg-gray-100 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                <div className="flex justify-center">
                    <img
                        src="/images/jack-logo.jpg"
                        alt="Jack Logo"
                        style={{ maxWidth: "80%", maxHeight: "30%" }}
                        className='pb-8'
                    />
                </div>
                <h2 className="text-2xl font-bold mb-6 text-center">Register</h2>
                {signupError && (
                    <p className="mt-2 text-sm text-red-500">{signupError}</p>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="firstName" className="block mb-2 font-bold text-gray-700">
                            First Name
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="lastName" className="block mb-2 font-bold text-gray-700">
                            Last Name
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            placeholder="Doe"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="email" className="block mb-2 font-bold text-gray-700">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            placeholder="name@host.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="username"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="password" className="block mb-2 font-bold text-gray-700">
                            Password
                        </label>
                        <input
                            type="password"
                            id="password"
                            autoComplete='new-password'
                            placeholder="Password"
                            value={password}
                            onChange={handlePasswordChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        {hasStartedTypingPassword && renderPasswordRules()}
                    </div>
                    <div className="mb-4">
                        <label htmlFor="confirmPassword" className="block mb-2 font-bold text-gray-700">
                            Confirm Password
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            autoComplete='new-password'
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!passwordsMatch && confirmPassword.length > 0 ? 'border-red-500' : ''}`}
                            required
                        />
                        {!passwordsMatch && confirmPassword.length > 0 && (
                            <p className="mt-2 text-sm text-red-500">Passwords do not match.</p>
                        )}
                    </div>
                    <div className="mb-6">
                        <label htmlFor="phone-input" className="block mb-2 font-bold text-gray-700">
                            Phone Number
                        </label>
                        <div className="flex items-center">
                            <button
                                id="dropdown-phone-button"
                                data-dropdown-toggle="dropdown-phone"
                                className="flex-shrink-0 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border border-gray-300 rounded-l-lg"
                                type="button"
                            >
                                <svg
                                    fill="none"
                                    aria-hidden="true"
                                    className="h-4 w-4 me-2"
                                    viewBox="0 0 20 15"
                                >
                                    <rect width="19.6" height="14" y="0.5" fill="#fff" rx="2"></rect>
                                    <mask
                                        id="a"
                                        style={{ maskType: "luminance" }}
                                        width="20"
                                        height="15"
                                        x="0"
                                        y="0"
                                        maskUnits="userSpaceOnUse"
                                    >
                                        <rect width="19.6" height="14" y="0.5" fill="#fff" rx="2"></rect>
                                    </mask>
                                    <g mask="url(#a)">
                                        <path
                                            fill="#D02F44"
                                            fillRule="evenodd"
                                            d="M19.6.5H0v.933h19.6V.5zm0 1.867H0V3.3h19.6v-.933zM0 4.233h19.6v.934H0v-.934zM19.6 6.1H0v.933h19.6V6.1zM0 7.967h19.6V8.9H0v-.933zm19.6 1.866H0v.934h19.6v-.934zM0 11.7h19.6v.933H0V11.7zm19.6 1.867H0v.933h19.6v-.933z"
                                            clipRule="evenodd"
                                        ></path>
                                        <path fill="#46467F" d="M0 .5h8.4v6.533H0z"></path>
                                        <g filter="url(#filter0_d_343_121520)">
                                            <path
                                                fill="url(#paint0_linear_343_121520)"
                                                fillRule="evenodd"
                                                d="M1.867 1.9a.467.467 0 11-.934 0 .467.467 0 01.934 0zm1.866 0a.467.467 0 11-.933 0 .467.467 0 01.933 0zm1.4.467a.467.467 0 100-.934.467.467 0 000 .934zM7.467 1.9a.467.467 0 11-.934 0 .467.467 0 01.934 0zM2.333 3.3a.467.467 0 100-.933.467.467 0 000 .933zm2.334-.467a.467.467 0 11-.934 0 .467.467 0 01.934 0zm1.4.467a.467.467 0 100-.933.467.467 0 000 .933zm1.4.467a.467.467 0 11-.934 0 .467.467 0 01.934 0zm-2.334.466a.467.467 0 100-.933.467.467 0 000 .933zm-1.4-.466a.467.467 0 11-.933 0 .467.467 0 01.933 0zM1.4 4.233a.467.467 0 100-.933.467.467 0 000 .933zm1.4.467a.467.467 0 11-.933 0 .467.467 0 01.933 0zm1.4.467a.467.467 0 100-.934.467.467 0 000 .934zM6.533 4.7a.467.467 0 11-.933 0 .467.467 0 01.933 0zM7 6.1a.467.467 0 100-.933.467.467 0 000 .933zm-1.4-.467a.467.467 0 11-.933 0 .467.467 0 01.933 0zM3.267 6.1a.467.467 0 100-.933.467.467 0 000 .933zm-1.4-.467a.467.467 0 11-.934 0 .467.467 0 01.934 0z"
                                                clipRule="evenodd"
                                            ></path>
                                        </g>
                                    </g>
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_343_121520"
                                            x1="0.933"
                                            x2="0.933"
                                            y1="1.433"
                                            y2="6.1"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#fff"></stop>
                                            <stop offset="1" stopColor="#F0F0F0"></stop>
                                        </linearGradient>
                                        <filter
                                            id="filter0_d_343_121520"
                                            width="6.533"
                                            height="5.667"
                                            x="0.933"
                                            y="1.433"
                                            colorInterpolationFilters="sRGB"
                                            filterUnits="userSpaceOnUse"
                                        >
                                            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
                                            <feColorMatrix
                                                in="SourceAlpha"
                                                result="hardAlpha"
                                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                            ></feColorMatrix>
                                            <feOffset dy="1"></feOffset>
                                            <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.06 0"></feColorMatrix>
                                            <feBlend
                                                in2="BackgroundImageFix"
                                                result="effect1_dropShadow_343_121520"
                                            ></feBlend>
                                            <feBlend
                                                in="SourceGraphic"
                                                in2="effect1_dropShadow_343_121520"
                                                result="shape"
                                            ></feBlend>
                                        </filter>
                                    </defs>
                                </svg>
                                +1
                            </button>
                            <div
                                id="dropdown-phone"
                                className="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-52 dark:bg-gray-700"
                            >
                                <ul className="py-2 text-sm text-gray-700 dark:text-gray-200" aria-labelledby="dropdown-phone-button">
                                    <li>
                                        <button
                                            type="button"
                                            className="inline-flex w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white"
                                            role="menuitem"
                                        >
                                            <div className="inline-flex items-center">
                                                <svg fill="none" aria-hidden="true" className="h-4 w-4 me-2" viewBox="0 0 20 15">
                                                    <rect width="19.6" height="14" y="0.5" fill="#fff" rx="2" />
                                                </svg>
                                                United States (+1)
                                            </div>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            <div className="relative w-full">
                                <input
                                    type="tel"
                                    id="phoneNumber"
                                    placeholder="2125551234"
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberChange}
                                    className={`block p-2.5 w-full z-20 text-sm text-gray-900 bg-gray-50 rounded-r-lg border-l-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-s-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:border-blue-500 ${
                                        !isPhoneNumberValid && phoneNumber.length > 0 ? 'border-red-500' : ''
                                    }`}
                                    required
                                />
                            </div>
                        </div>
                        {!isPhoneNumberValid && phoneNumber.length > 0 && (
                            <p className="mt-2 text-sm text-red-500">Phone number must be 10 digits.</p>
                        )}
                    </div>
                    <div className="mb-4">
                        <label htmlFor="dateOfBirth" className="block mb-2 font-bold text-gray-700">
                            Date of Birth
                        </label>
                        <ImprovedDatePicker
                            id="dateOfBirth"
                            value={dateOfBirth}
                            onChange={handleDateOfBirthChange}
                            className="w-full"
                            hasError={!isDateOfBirthValid && dateOfBirth.length > 0}
                            required
                            placeholder="mm/dd/yyyy"
                        />
                        {!isDateOfBirthValid && dateOfBirth.length > 0 && (
                            <p className="mt-2 text-sm text-red-500">You must be at least 18 years old to register.</p>
                        )}
                    </div>
                    <div className="mb-6">
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="terms"
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300"
                                    required
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="terms" className="font-medium text-gray-900">
                                    I agree to JackAtHome's{' '}
                                    <a href="https://jackathome.com/pages/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        Terms of Service
                                    </a>{' '}
                                    and{' '}
                                    <a href="https://jackathome.com/pages/privacy-policy-hipaa-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                        Privacy Policy
                                    </a>
                                    {' '}. By providing my phone and email address, I agree JackAtHome may contact me, which may include SMS notifications. Message and data rates may apply.
                                </label>
                            </div>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className={`w-full bg-[#43a4db] text-white font-bold py-2 px-4 rounded-md hover:bg-[#2a6e95] transition duration-300 
                                    ${!isPasswordValid || !isPhoneNumberValid || !isDateOfBirthValid || !agreedToTerms || !passwordsMatch ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' : ''}`}
                        disabled={!isPasswordValid || !isPhoneNumberValid || !isDateOfBirthValid || !agreedToTerms || !passwordsMatch}
                    >
                        Register
                    </button>
                </form>
                <div className="mb-2 pt-4 text-center">
                    <span className="text-gray-600">Already have an account? </span>
                    <a href="/login" className="text-blue-600 hover:underline text-center">
                        Login here
                    </a>
                </div>
            </div>
        </div>
    );
};

export default RegistrationForm;